import * as React from 'react';
import {useState} from 'react';
import classnames from 'classnames';

import {EpiImage} from "features/search/types";
import {useDeviceStore, selectIsMobile} from 'features/ui/store/useDeviceStore';
import {Margin, HeadlineLevel} from 'features/ui/enums';
import {IContentBlock} from 'features/blocks/types';
import {sanitizeHtml} from 'features/services/utils/sanitize-html';

import 'components/blocks/FAQ/FaqQuestionBlock/style.scss';

import {P} from '@atoms/Text/P';
import {Icon} from '@atoms/Media/Icon';
import {VideoBlock} from "components/blocks/ImageAndVideo/VideoBlock";
import {GenericMediaFileBlockType} from 'components/blocks/Link/GenericMediaFileBlock';
import {Headline} from '@atoms/Text/Headline';

const GenericMediaFileBlock = React.lazy(() =>
    import('components/blocks/Link/GenericMediaFileBlock').then(module => ({ default: module.GenericMediaFileBlock }))
);

export interface FaqQuestionBlockType {
    question: string;
    answer: string;
    youtubeLink?: string;
    youtubeImage?: EpiImage;
    youtubeDescription?: string;
    youtubeScriptText?: string;
    files?: IContentBlock<GenericMediaFileBlockType>[];
}

interface Props extends IContentBlock<FaqQuestionBlockType>{
    questionId?: string;
}

export const FaqQuestionBlock: React.FunctionComponent<Props> = (props) => {
    const isMobile = useDeviceStore(selectIsMobile);
    const [isOpen, setIsOpen] = useState<boolean>(false);
    // Frågor i en FAQ-sektion renderas utanför ContentArea och får inget id, men är egna Episerver-block
    const blockId = props.id ?? (props.contentId ? `block-${props.contentId}` : undefined);

    const questionClasses = classnames('faq-question-block', {
        'faq-question-block--open': isOpen,
    });
    const handleOpen = () => {
        setIsOpen(!isOpen);
    };

    return (
        <div className={questionClasses} id={blockId}>
            <div
                className="faq-question-block__header"
                onClick={handleOpen}
                aria-expanded={isOpen}
                aria-controls={props.questionId}
                role='button'
                tabIndex={0}
                onKeyPress={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') handleOpen();
                }}
            >
                {isMobile 
                    ? <P margin={Margin.NONE} >{props.data.question}</P> 
                    : <Headline level={HeadlineLevel.H5} margin={Margin.NONE}>{props.data.question}</Headline>
                }
                <Icon name={isOpen ? "minus" : "plus" } size={20} />
            </div>

            <div className="faq-question-block__content" id={props.questionId} aria-hidden={!isOpen}>
                <div dangerouslySetInnerHTML={{__html: sanitizeHtml(props.data.answer)}}  className="faq-question-block__content__html" />

                {props.data.youtubeLink && props.data.youtubeImage &&
                    <VideoBlock
                        data={{
                            videoUrl: props.data.youtubeLink,
                            title: '',
                            image: props.data.youtubeImage,
                            renderOnPage: true,
                            description: props.data.youtubeDescription,
                            scriptText: props.data.youtubeScriptText,
                        }} 
                        component={''} 
                        renderStyle={''} 
                    />
                }

                {props.data.files && props.data.files.length > 0 &&
                    <div className="faq-question-block__files">
                        {props.data.files.map((file, index) => {
                            return (
                                <React.Suspense fallback={<div></div>} key={`mediafile-${index}`}>
                                    <GenericMediaFileBlock {...file} />
                                </React.Suspense>
                            )

                        })}
                    </div>
                }
            </div>
        </div>
    );
};